import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {Box, Grid, Stack, SxProps, Typography} from '@mui/material';
import {IMachineDTO, MachineDTO} from 'api/api';
import {useAuth} from 'auth/auth-provider';
import {localized, localizedInterpolation} from 'i18n/i18n';
import {useDialog} from 'providers/dialog-provider';
import React, {FC, useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useDeleteMachineMutation, useUpsertMachineNicknameMutation} from 'state/features/api/machines-slice';
import {selectCurrentUser, useRemoveMachineFromUserMutation} from 'state/features/api/user-slice';
import {routes} from 'state/routes';
import {useGoBackOnSuccess} from 'utilities/navigation-helper';
import {AsyncOperationHandler} from 'utilities/platform-helpers/api-helper';
import {nameof} from 'utilities/platform-helpers/nameof-helper';
import {BlobImage} from 'view/components/default-components/blob-image';
import {BasicButton} from 'view/components/default-components/buttons/basic-button';
import {FlexRow} from 'view/components/default-components/flex-row';
import {Form} from 'view/components/default-components/form/form';
import {TextInput} from 'view/components/default-components/form/text-input';
import {PageHeader} from 'view/components/default-components/page-header.tsx/page-header';
import MachineConnection from 'view/components/specific-components/machine/current-state/machine-connection';
import {MachineState} from 'view/components/specific-components/machine/current-state/machine-state';
import {ResourcesMenu} from './resources-menu';

interface Props {
  machine: IMachineDTO | undefined;
}

const sharedCacheKey = 'statusHeaderMutation';
export const StatusHeader: FC<Props> = React.memo((props) => {
  const {machine} = props;
  const {isGlobalAdmin, isAgent} = useAuth();
  const currentUser = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const {openDeleteDialog, openConfirmDialog} = useDialog();
  const [deleteMachine, {isLoading, isSuccess, reset}] = useDeleteMachineMutation({fixedCacheKey: sharedCacheKey});
  const [removeMachineFromUser] = useRemoveMachineFromUserMutation({fixedCacheKey: sharedCacheKey});
  const [upsertNickname, {isLoading: nicknameLoading}] = useUpsertMachineNicknameMutation();
  const [formDefaultValues, setFormDefaultValues] = React.useState<IMachineDTO | undefined>(machine);

  const goToEditMachine = useCallback(() => {
    if (!machine) return;
    navigate(`${routes.machines}/${machine.id}/edit`);
  }, [machine, navigate]);

  const handleDeleteConfirm = useCallback(() => {
    if (!machine?.serialNumber) return;
    deleteMachine(machine.serialNumber);
  }, [deleteMachine, machine?.serialNumber]);

  const handleRemoveConfirm = useCallback(() => {
    if (!machine || !currentUser) return;

    removeMachineFromUser({machineId: machine.id, userId: currentUser.id});
  }, [currentUser, machine, removeMachineFromUser]);

  const handleRemove = useCallback(() => {
    openConfirmDialog(localized('RemoveMachineConfirm'), handleRemoveConfirm);
  }, [handleRemoveConfirm, openConfirmDialog]);

  const handleDelete = useCallback(() => {
    openDeleteDialog(localized('ConfirmDeleteMachine'), handleDeleteConfirm);
  }, [handleDeleteConfirm, openDeleteDialog]);

  const handleUpsertNickname = useCallback(
    async (data: MachineDTO) => {
      if (!data.nickName) return;

      return await AsyncOperationHandler(
        () => upsertNickname({nickname: data.nickName!, serialNumber: data.serialNumber}),
        true,
      );
    },
    [upsertNickname],
  );

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useGoBackOnSuccess(isSuccess, reset);

  useEffect(() => {
    // Because we poll every 10 seconds, we need to update the default values only when a change is actually made.
    // In this case, changes can only be made to nickname, so we do a simple solution for checking for that.
    if (!formDefaultValues || machine?.nickName !== formDefaultValues.nickName) {
      setFormDefaultValues(machine);
    }
  }, [machine, formDefaultValues]);

  const {serialNumber, machineTypeImageUrl, machineStateDto, machineTypeName} = machine ?? {};

  return (
    <PageHeader area="machineDetails">
      <Grid container spacing={{xs: 5, xl: 6}} sx={{paddingBottom: 1}}>
        <Grid item xs={12} md>
          <Stack direction="row">
            {machineTypeImageUrl && (
              <BlobImage alt="Machine Image" src={`/images/machines/${machineTypeImageUrl}`} maxWidth={115} />
            )}

            <Stack sx={{paddingLeft: 5}} justifyContent="space-between">
              <Box
                sx={{display: 'flex', alignItems: 'center', marginLeft: '-6px', cursor: 'pointer'}}
                onClick={navigateBack}>
                <KeyboardArrowLeftIcon color="primary"></KeyboardArrowLeftIcon>
                <Typography color="common.flexoWashBlue" marginTop="1.4px" variant="body2">
                  {localized('Back')}
                </Typography>
              </Box>
              <Stack>
                <Typography color="common.flexoWashBlue" variant="h4">
                  {machineTypeName}
                </Typography>
                <Typography variant="body3" color="common.flexoWashBlueLight">
                  #{serialNumber ?? 'Unknown'}
                </Typography>
              </Stack>
              <FlexRow gap={2} mt={2}>
                <ResourcesMenu machine={machine} />
                {isGlobalAdmin && (
                  <>
                    <BasicButton
                      disabled={isLoading}
                      onClick={goToEditMachine}
                      text={localizedInterpolation('EditInt', 'Machine')}
                    />
                    <BasicButton
                      disabled={isLoading}
                      buttonColor="error"
                      onClick={handleDelete}
                      text={localized('DeleteMachine')}
                    />
                  </>
                )}
                {!isGlobalAdmin && !isAgent && (
                  <BasicButton
                    disabled={isLoading}
                    buttonColor="error"
                    onClick={handleRemove}
                    text={localizedInterpolation('RemoveInt', 'Machine')}
                  />
                )}
              </FlexRow>
              <FlexRow mt={2}>
                <Form defaultValues={formDefaultValues} onSubmit={handleUpsertNickname}>
                  <TextInput
                    name={nameof<IMachineDTO>('nickName')}
                    label={localized('Nickname')}
                    disabled={!machine || nicknameLoading}
                    inputSx={styles.nicknameInput}
                    sx={styles.nickname}
                  />
                </Form>
              </FlexRow>
            </Stack>
          </Stack>
        </Grid>

        <Grid container item xs={12} md sx={{display: 'flex', alignItems: 'flex-end'}}>
          <Grid
            container
            item
            columnGap={[6, 5]}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
            }}>
            <Grid item xs>
              {machineStateDto?.machineStatus !== undefined && (
                <MachineState variant="machine-details" machineCurrentState={machineStateDto} />
              )}
            </Grid>
            <Grid item xs>
              {machineStateDto?.connectionStatus !== undefined && (
                <MachineConnection
                  iconPlacement="left"
                  variant="machine-details"
                  latestHeartbeat={machineStateDto.latestConnectionStateChange}
                  connectionState={machineStateDto.connectionStatus}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </PageHeader>
  );
});

const styles: {[key: string]: SxProps} = {
  nicknameInput: {
    height: 38,
    width: '70%',
  },
  nickname: {
    mt: 0,
  },
};
